import React, { useState, useEffect, useCallback } from "react";
import {
  Grid,
  Paper,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  CircularProgress,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
  LineChart,
  Line,
} from "recharts";
import moment from "moment";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const useStyles = makeStyles((theme) => ({
  root: {
    flex: 1,
    padding: theme.spacing(2),
    overflowY: "auto",
    ...theme.scrollbarStyles,
  },
  filterRow: {
    display: "flex",
    gap: theme.spacing(1.5),
    alignItems: "center",
    flexWrap: "wrap",
    marginBottom: theme.spacing(3),
  },
  filterField: {
    minWidth: 140,
  },
  chartCard: {
    padding: theme.spacing(2.5),
    borderRadius: 12,
    height: "100%",
  },
  chartTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(2),
    fontSize: "0.95rem",
    color: theme.palette.text.primary,
  },
  emptyChart: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    height: 240,
    color: "#9e9e9e",
    fontSize: "0.875rem",
  },
  legendRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    marginTop: theme.spacing(1.5),
  },
  legendItem: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    fontSize: "0.78rem",
    color: theme.palette.text.secondary,
  },
  legendDot: {
    width: 10,
    height: 10,
    borderRadius: "50%",
    flexShrink: 0,
  },
}));

const fmtBRL = (v) =>
  Number(v || 0).toLocaleString("pt-br", { style: "currency", currency: "BRL" });

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <Paper style={{ padding: "8px 12px", fontSize: "0.8rem" }} elevation={3}>
      <div style={{ fontWeight: 600, marginBottom: 4 }}>{label}</div>
      {payload.map((p) => (
        <div key={p.name} style={{ color: p.color }}>
          {p.name}: {fmtBRL(p.value)}
        </div>
      ))}
    </Paper>
  );
};

const defaultDateFrom = () => moment().startOf("month").format("YYYY-MM-DD");
const defaultDateTo   = () => moment().endOf("month").format("YYYY-MM-DD");

const RelatorioPanel = () => {
  const classes = useStyles();

  const [dateFrom, setDateFrom] = useState(defaultDateFrom);
  const [dateTo, setDateTo]     = useState(defaultDateTo);
  const [catType, setCatType]   = useState("income");

  const [catData, setCatData]       = useState([]);
  const [cashFlow, setCashFlow]     = useState(null);
  const [loadingCat, setLoadingCat] = useState(false);
  const [loadingCf, setLoadingCf]   = useState(false);

  const loadCategory = useCallback(async () => {
    setLoadingCat(true);
    try {
      const { data } = await api.get("/transactions/report/category", {
        params: { dateFrom, dateTo, type: catType },
      });
      setCatData(data);
    } catch (err) {
      toastError(err);
    } finally {
      setLoadingCat(false);
    }
  }, [dateFrom, dateTo, catType]);

  const loadCashFlow = useCallback(async () => {
    setLoadingCf(true);
    try {
      const { data } = await api.get("/transactions/report/cashflow", {
        params: { dateFrom, dateTo },
      });
      setCashFlow(data);
    } catch (err) {
      toastError(err);
    } finally {
      setLoadingCf(false);
    }
  }, [dateFrom, dateTo]);

  useEffect(() => {
    loadCategory();
    loadCashFlow();
  }, [loadCategory, loadCashFlow]);

  const barData = catData.map((c) => ({
    name: c.name,
    Total: Number(c.total),
    Pago: Number(c.paid),
    fill: c.color,
  }));

  const pieData = catData.map((c) => ({
    name: c.name,
    value: Number(c.total),
    color: c.color,
  }));

  const cfItems = cashFlow?.items ?? [];

  return (
    <Paper className={classes.root} variant="outlined">
      {/* Filtros */}
      <div className={classes.filterRow}>
        <TextField
          label="De"
          type="date"
          size="small"
          InputLabelProps={{ shrink: true }}
          value={dateFrom}
          onChange={(e) => setDateFrom(e.target.value)}
          className={classes.filterField}
        />
        <TextField
          label="Até"
          type="date"
          size="small"
          InputLabelProps={{ shrink: true }}
          value={dateTo}
          onChange={(e) => setDateTo(e.target.value)}
          className={classes.filterField}
        />
      </div>

      <Grid container spacing={3}>
        {/* ── Fluxo de Caixa (linha) ── */}
        <Grid item xs={12}>
          <Paper className={classes.chartCard} variant="outlined">
            <Typography className={classes.chartTitle}>Fluxo de Caixa</Typography>
            {loadingCf ? (
              <div className={classes.emptyChart}><CircularProgress size={32} /></div>
            ) : cfItems.length === 0 ? (
              <div className={classes.emptyChart}>Nenhum dado no período.</div>
            ) : (
              <ResponsiveContainer width="100%" height={240}>
                <LineChart data={cfItems} margin={{ top: 4, right: 16, left: 8, bottom: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="period" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => fmtBRL(v)} width={90} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Line type="monotone" dataKey="income"  name="Receitas"  stroke="#2e7d32" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="expense" name="Despesas"  stroke="#c62828" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="balance" name="Saldo Acumulado" stroke="#1565c0" strokeWidth={2} strokeDasharray="5 3" dot={false} />
                </LineChart>
              </ResponsiveContainer>
            )}
          </Paper>
        </Grid>

        {/* ── Por Categoria: Barras ── */}
        <Grid item xs={12} md={7}>
          <Paper className={classes.chartCard} variant="outlined">
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
              <Typography className={classes.chartTitle} style={{ marginBottom: 0 }}>
                Por Categoria
              </Typography>
              <FormControl size="small" style={{ minWidth: 110 }}>
                <Select value={catType} onChange={(e) => setCatType(e.target.value)}>
                  <MenuItem value="income">Receitas</MenuItem>
                  <MenuItem value="expense">Despesas</MenuItem>
                </Select>
              </FormControl>
            </div>

            {loadingCat ? (
              <div className={classes.emptyChart}><CircularProgress size={32} /></div>
            ) : barData.length === 0 ? (
              <div className={classes.emptyChart}>Nenhum dado no período.</div>
            ) : (
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={barData} margin={{ top: 4, right: 16, left: 8, bottom: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => fmtBRL(v)} width={90} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="Total" name="Total" radius={[4, 4, 0, 0]}>
                    {barData.map((entry, i) => (
                      <Cell key={i} fill={entry.fill} fillOpacity={0.85} />
                    ))}
                  </Bar>
                  <Bar dataKey="Pago" name="Pago" radius={[4, 4, 0, 0]}>
                    {barData.map((entry, i) => (
                      <Cell key={i} fill={entry.fill} fillOpacity={0.45} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </Paper>
        </Grid>

        {/* ── Por Categoria: Pizza ── */}
        <Grid item xs={12} md={5}>
          <Paper className={classes.chartCard} variant="outlined">
            <Typography className={classes.chartTitle}>
              Distribuição — {catType === "income" ? "Receitas" : "Despesas"}
            </Typography>

            {loadingCat ? (
              <div className={classes.emptyChart}><CircularProgress size={32} /></div>
            ) : pieData.length === 0 ? (
              <div className={classes.emptyChart}>Nenhum dado no período.</div>
            ) : (
              <>
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie
                      data={pieData}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      outerRadius={80}
                      innerRadius={40}
                    >
                      {pieData.map((entry, i) => (
                        <Cell key={i} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(v) => fmtBRL(v)} />
                  </PieChart>
                </ResponsiveContainer>
                <div className={classes.legendRow}>
                  {pieData.map((item) => (
                    <div key={item.name} className={classes.legendItem}>
                      <div className={classes.legendDot} style={{ backgroundColor: item.color }} />
                      {item.name}: {fmtBRL(item.value)}
                    </div>
                  ))}
                </div>
              </>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default RelatorioPanel;
